import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonType } from 'projects/ppb-shared-library/src/lib/models/button-config.model';
import { BehaviorSubject } from 'rxjs';
import { ServerConfigModel } from '../../../technical/version/server-config.model';
import { AccountActivationService } from '../account-activation.service';

@Component({
  selector: 'ppb-account-activation',
  templateUrl: './ppb-account-activation.component.html',
  styleUrls: ['./ppb-account-activation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PpbAccountActivationComponent implements OnInit {

  loginForm: UntypedFormGroup;
  step: number = 1;
  configuration: ServerConfigModel;
  isLinkCheckLoading = false;
  isLinkValid = false;
  isLoading$ = new BehaviorSubject(false);
  ButtonType = ButtonType;
  rulesAlreadyAccepted: boolean;
  policiesAlreadyAccepted: boolean;

  private error: string;
  private link: string;
  private activationUuid: string;
  private smsUuid: string;

  constructor(
    private fb: UntypedFormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private userService: AccountActivationService,
    private cd: ChangeDetectorRef,
  ) {
  }

  ngOnInit(): void {
    this.link = this.route.snapshot.queryParams.link || undefined;
    this.loginForm = this.fb.group({
      sms: new UntypedFormControl('', { validators: [Validators.required] }),
      rulesAgreementControl: new UntypedFormControl('', { validators: [Validators.requiredTrue] }),
      policiesAgreementControl: new UntypedFormControl('', { validators: [Validators.requiredTrue] })
    });
    this.userService.accountActivationStart(this.link)
      .subscribe(res => {
          this.activationUuid = res.token;
          this.rulesAlreadyAccepted = res.rulesAccepted;
          this.policiesAlreadyAccepted = res.policyAccepted;
          console.log('ACTIVATION STEP 1 RESPONSE', res);
          this.isLinkValid = true;
          this.cd.markForCheck();
        },
        error => {
          console.log('ERROR', error);
          this.isLinkValid = false;
        });
    this.configuration = this.route.snapshot.data.configuration;
    this.isLinkCheckLoading = false;
  }

  navigateToHome(): void {
    this.router.navigate(['/']);
  }

  isError(): boolean {
    const errorMessages = this.errorMessage();
    return errorMessages !== undefined && errorMessages.length > 0;
  }

  errorMessage(): string[] {
    if (this.error) {
      return [this.error];
    }
    if (this.link === undefined) {
      return [$localize`:@@account.code_invalid:Link nie zawiera prawidłowego kodu`];
    }
    return [];
  }

  clearBeforeNextStep(): void {
    this.error = undefined;
  }

  nextStep(step: number): void {
    const rules = this.loginForm.controls.rulesAgreementControl;
    const policies = this.loginForm.controls.policiesAgreementControl;
    this.clearBeforeNextStep();
    switch (step) {
      case 2:
        rules.markAsTouched();
        rules.updateValueAndValidity();
        policies.markAsTouched();
        policies.updateValueAndValidity();
        const rulesAccepted = this.rulesAlreadyAccepted || rules.valid;
        const policiesAccepted = this.policiesAlreadyAccepted || policies.valid;
        if (rulesAccepted && policiesAccepted) {
          this.isLoading$.next(true);
          this.userService.accountActivationPassword(this.activationUuid)
            .subscribe(res => {
                this.step = 2;
                this.smsUuid = res.token;
                this.isLoading$.next(false);
                this.cd.markForCheck();
              }, _ => this.isLoading$.next(false)
            );
        }
        break;
      case 3:
        const smsControl = this.loginForm.controls.sms;
        this.isLoading$.next(true);
        const rulesAcceptedValue = this.rulesAlreadyAccepted || rules.value;
        const policiesAcceptedValue = this.policiesAlreadyAccepted || policies.value;
        this.userService.accountActivationSms(this.smsUuid, smsControl.value, policiesAcceptedValue, rulesAcceptedValue)
          .subscribe(res => {
              this.smsUuid = res.token;
              this.step = 3;
              this.cd.markForCheck();
            }, _error => {
              this.error = $localize`:@@account.sms_invalid:Nieprawidłowy kod sms`;
              this.cd.markForCheck();
            }, () =>
              this.isLoading$.next(false)
          );
        break;
      default:
        this.step = step;
        break;
    }
  }
}
